// ============================================
// RESULTS API SERVICE
// ============================================

import type {
    InstanceResults,
    TemplateResults,
    RespondentResult,
    OrganizationResults,
} from '../types/results';
import { apiFetch } from '../utils/api-client';

/**
 * Unwrap the result object from n8n's response format
 * The SQL queries return { result: {...} }
 */
function unwrapResult<T>(data: any): T {
    // Handle array responses: [{ result: {...} }]
    if (Array.isArray(data) && data.length > 0) {
        return unwrapResult<T>(data[0]);
    }
    if (data && typeof data === 'object' && 'result' in data) {
        return data.result as T;
    }
    return data as T;
}

/**
 * Helper to extract respondents array from various potential API structures
 */
function extractRespondents(raw: any): any[] {
    if (!raw) return [];

    // Handle array responses: [{ result: { respondents: [...] } }]
    if (Array.isArray(raw) && raw.length > 0 && raw[0] && typeof raw[0] === 'object' && ('result' in raw[0] || 'respondents' in raw[0])) {
        return extractRespondents(raw[0]);
    }

    // If it's already an array of respondents
    if (Array.isArray(raw)) return raw;

    // Support { result: [...] } or { result: { respondents: [...] } }
    if (raw.result) {
        if (Array.isArray(raw.result)) return raw.result;
        if (raw.result.respondents && Array.isArray(raw.result.respondents)) return raw.result.respondents;
        if (raw.result.data && Array.isArray(raw.result.data)) return raw.result.data;
    }

    // Support root-level { respondents: [...] }, { data: [...] }, { items: [...] }
    if (raw.respondents && Array.isArray(raw.respondents)) return raw.respondents;
    if (raw.data && Array.isArray(raw.data)) return raw.data;
    if (raw.items && Array.isArray(raw.items)) return raw.items;

    return [];
}

/**
 * Helper to extract specific root fields (like answer_scale or survey_name)
 */
function extractRootField(raw: any, field: string): any {
    if (!raw) return undefined;
    // Handle array responses: [{ result: {...} }]
    if (Array.isArray(raw) && raw.length > 0) {
        return extractRootField(raw[0], field);
    }
    if (raw[field] !== undefined) return raw[field];
    if (raw.result && raw.result[field] !== undefined) return raw.result[field];
    return undefined;
}

/**
 * Map respondent with 'scores' field to use 'category_stats' for compatibility
 */
function mapRespondentScores(respondent: any): any {
    if (!respondent) return respondent;

    // If respondent has 'scores' but not 'category_stats', map it
    if (respondent.scores && !respondent.category_stats) {
        return {
            ...respondent,
            category_stats: respondent.scores,
        };
    }
    return respondent;
}

/**
 * Get all results for a specific survey instance
 */
export async function getInstanceResults(instanceId: string): Promise<InstanceResults> {
    const raw = await apiFetch<any>(`/admin/results/survey-instance?instance_id=${instanceId}`);

    const respondents = extractRespondents(raw).map(mapRespondentScores);
    const answer_scale = extractRootField(raw, 'answer_scale');

    // Handle nested instance object from new SQL-First response
    const instanceData = extractRootField(raw, 'instance');
    const name = instanceData?.name || extractRootField(raw, 'survey_name') || extractRootField(raw, 'instance_name') || 'סקר';

    const ageAverages = extractRootField(raw, 'ageAverages') || undefined;
    const genderAverages = extractRootField(raw, 'genderAverages') || undefined;
    const demographicAverages = extractRootField(raw, 'demographicAverages') || undefined;

    return {
        instance_id: instanceData?.id || extractRootField(raw, 'instance_id') || instanceId,
        survey_name: name,
        instance_name: instanceData?.name || extractRootField(raw, 'instance_name'),
        template_id: instanceData?.template_id || extractRootField(raw, 'template_id'),
        organization_id: instanceData?.organization_id || extractRootField(raw, 'organization_id'),
        is_global: instanceData?.is_global,
        created_at: instanceData?.created_at || extractRootField(raw, 'created_at') || '',
        respondents: respondents,
        respondentCount: extractRootField(raw, 'respondentCount'),
        showRespondents: extractRootField(raw, 'showRespondents'),
        instanceAverages: extractRootField(raw, 'instanceAverages'),
        organizationAverages: extractRootField(raw, 'organizationAverages'),
        globalAverages: extractRootField(raw, 'globalAverages'),

        globalPercentiles: extractRootField(raw, 'globalPercentiles'),
        answer_scale: answer_scale,

        // Demographic averages (for Group View)
        ageAverages,
        genderAverages,
        demographicAverages,
    };
}

/**
 * Get all results for all instances of a survey template
 */
export async function getTemplateResults(templateId: number): Promise<TemplateResults> {
    const raw = await apiFetch<any>(`/admin/results/survey-template?template_id=${templateId}`);

    const respondents = extractRespondents(raw).map(mapRespondentScores);
    const answer_scale = extractRootField(raw, 'answer_scale');

    return {
        template_id: extractRootField(raw, 'template_id') || templateId,
        template_name: extractRootField(raw, 'template_name') || 'תבנית',
        respondents: respondents,
        globalAverages: extractRootField(raw, 'globalAverages'),

        globalPercentiles: extractRootField(raw, 'globalPercentiles'),
        answer_scale: answer_scale,
    };
}

/**
 * Get all results for all respondents in an organization
 */
export async function getOrganizationResults(organizationId: string): Promise<OrganizationResults> {
    const raw = await apiFetch<any>(`/admin/results/organization/?organization_id=${organizationId}`);

    // Handle nested organization object from new SQL-First response
    const orgData = extractRootField(raw, 'organization');

    // New SQL-First API returns instances array instead of respondents
    const instances = extractRootField(raw, 'instances') || [];
    const respondents = extractRespondents(raw).map(mapRespondentScores);
    const answer_scale = extractRootField(raw, 'answer_scale');

    return {
        organization_id: orgData?.id || extractRootField(raw, 'organization_id') || organizationId,
        organization_name: orgData?.name || extractRootField(raw, 'organization_name') || 'ארגון',
        is_global: orgData?.is_global,
        instances: instances,
        respondents: respondents,
        instanceCount: extractRootField(raw, 'instanceCount'),
        totalRespondents: extractRootField(raw, 'totalRespondents'),
        organizationAverages: extractRootField(raw, 'organizationAverages'),
        globalAverages: extractRootField(raw, 'globalAverages'),

        globalPercentiles: extractRootField(raw, 'globalPercentiles'),
        answer_scale: answer_scale,

        ageAverages: extractRootField(raw, 'ageAverages') || undefined,
        genderAverages: extractRootField(raw, 'genderAverages') || undefined,
        demographicAverages: extractRootField(raw, 'demographicAverages') || undefined,
    };
}

/**
 * Get full results for a single respondent
 */
export async function getRespondentResults(respondentId: string): Promise<RespondentResult> {
    const raw = await apiFetch<any>(`/admin/results/respondent/?respondent_id=${respondentId}`);
    const unwrapped = unwrapResult<any>(raw);

    // SQL-First response has { respondent: {...}, scores: {...}, ... }
    // Map to expected RespondentResult structure
    const respondentData = unwrapped.respondent || unwrapped;
    const scores = unwrapped.scores || unwrapped.category_stats || null;

    return {
        id: respondentData.id || unwrapped.id || respondentId,
        name: respondentData.name || unwrapped.name || 'Unknown',
        email: respondentData.email || unwrapped.email || '',
        status: respondentData.status || unwrapped.status || 'done',
        instance_id: respondentData.survey_instance_id || respondentData.instance_id || unwrapped.instance_id,
        template_id: respondentData.template_id || unwrapped.template_id,
        organization_id: respondentData.organization_id || unwrapped.organization_id,
        instance_name: unwrapped.instance_name,
        date: unwrapped.date,
        category_stats: scores,
        answers: unwrapped.answers || null,
        answer_scale: unwrapped.answer_scale || raw.answer_scale,
        instanceAverages: unwrapped.instanceAverages || null,
        globalAverages: unwrapped.globalAverages || null,

        globalPercentiles: unwrapped.globalPercentiles || null,
        instancePercentiles: unwrapped.instancePercentiles || null,

        // Demographic fields
        date_of_birth: respondentData.date_of_birth ?? undefined,
        gender: respondentData.gender ?? undefined,
        age_bucket: respondentData.age_bucket ?? undefined,
        gender_normalized: respondentData.gender_normalized ?? undefined,
        ageGroupAverages: unwrapped.ageGroupAverages ?? undefined,
        genderGroupAverages: unwrapped.genderGroupAverages ?? undefined,
        ageGroupPercentiles: unwrapped.ageGroupPercentiles ?? undefined,
        genderGroupPercentiles: unwrapped.genderGroupPercentiles ?? undefined,
        ageGroupCount: unwrapped.ageGroupCount ?? undefined,
        genderGroupCount: unwrapped.genderGroupCount ?? undefined,
    };
}
